// Cross-app auth sync (canvas ↔ workspace) — the SDK-token cookie/iframe
// handoff. Lets a user signed in on one Symbols origin (canvas) skip signin
// on a peer origin (workspace / marketplace / preview) by forwarding the SDK
// tokens held in localStorage. Consumed by `shared/functions/sdk.js`
// (hydrateAuthFromCookie + installAuthSync) and the `/_session-bridge` pages
// (collectSdkTokens).

// `@symbo.ls/sdk-supabase-bridge` was DELETED with the Supabase plane
// (2026-07-27) and this implementation ported verbatim into the SDK
// (sdk/src/crossAppAuth.js, exported both from the main entry and the
// `./cross-app-auth` subpath). The old specifier kept resolving ONLY because a
// stale copy was still hoisted into the repo-root node_modules — on a clean
// install it is absent, Parcel fails to resolve it, and the workspace shell
// does not boot at all. This import has been corrected twice (ed3dfd87,
// 2cfc3cc6) and reverted twice; see crossAppAuth.regression.test.js, which
// fails if the dead specifier ever comes back.
// NB: the MAIN entry, not the `./cross-app-auth` subpath the SDK also exports.
// Parcel's default resolver does not honour `exports` subpath maps — it tries to
// load a literal `./cross-app-auth` file and fails the build
// ("Cannot load file './cross-app-auth' from module '@symbo.ls/sdk'"). The main
// entry re-exports createCrossAppAuth (sdk/src/index.js), and prefs.js already
// imports from it the same way.
import { createCrossAppAuth } from '@symbo.ls/sdk'
import { sessionBridgePeerOrigin } from './functions/sessionBridgePeerOrigin.js'

// ── Cross-app auth (canvas ↔ workspace cookie + iframe sync) ─────────

// Peer resolution lives in ./functions/sessionBridgePeerOrigin.js — the
// access-only re-ask (./functions/sessionBridgeReacquire.js) frames the same
// peer, so one resolver serves both.
const _crossApp = createCrossAppAuth({ resolvePeerOrigin: sessionBridgePeerOrigin })

// DEFERRED-CALL WRAPPERS, never `export const x = _crossApp.x`.
//
// A bare member alias has no body for frank to emit. `_crossApp` is the
// return of a factory imported from `@symbo.ls/sdk`, which frank stubs at
// bundle time, so frank had no value to read the member off and published
// each alias as the EMPTY STRING. `functions/sdkClient.js` calls all three
// inside `getSDK`/`awaitSDKInit`, so frank hoists them into the emitted
// `globalScope` and every published surface threw
// `TypeError: <name> is not a function` at boot
// (DESIGN-CROSSAPPAUTH-PUBLISHES-EMPTY-1, measured on
// system--workspace-ui.at.symbo.ls).
//
// The wrapper form gives frank a real function body, and its `_crossApp`
// read is now a CALL-TIME read that frank rewrites onto its third
// resolution tier: `globalThis.__SMBLS_PKGS__['@symbo.ls/sdk']`, the
// HOST-REGISTERED package the page runtime installs before any project code
// runs (smbls/plugins/frank/scopeRewriter.js `HOST_REGISTERED_PACKAGES`;
// Nika, DECISIONS.md 2026-08-20 FRAMEWORK-REGISTRY-RESTRICTED-IMPORTS-1
// answer B — the platform, not smbls, owns the platform client).
//
// A wrapper and NOT the self-contained inline copy `collectSdkTokens` uses
// below, because these three must share ONE instance with the SDK's:
// `installAuthSync` patches `localStorage.setItem/removeItem/clear` and
// keeps the `_installed`/`_uninstall` snapshot in the factory closure, so a
// second independent copy would double-patch the same storage and the SDK's
// own `uninstallAuthSync` could not undo it. `collectSdkTokens` is a pure
// read with no shared state, which is why the copy is safe there and not
// here. All wrappers below resolve through the same emitted `__scope`, so
// one `_crossApp` instance serves them all at a frank-served runtime too.
export const hydrateAuthFromCookie = (...a) => _crossApp.hydrateAuthFromCookie(...a)
export const hydrateAuthFromIframeBridge = (...a) =>
  _crossApp.hydrateAuthFromIframeBridge(...a)
export const persistAuthToCookie = (...a) => _crossApp.persistAuthToCookie(...a)
export const clearAuthCookie = (...a) => _crossApp.clearAuthCookie(...a)
export const installAuthSync = (...a) => _crossApp.installAuthSync(...a)

// A VALUE, so no wrapper can defer it — a member alias here would publish as
// "" exactly like the five above did. Self-contained literal instead, the
// same recipe as `collectSdkTokens`. Keep it equal to the SDK's
// `DEFAULT_TOKEN_KEYS` (sdk/src/crossAppAuth.js) and to the list inside
// `collectSdkTokens` below.
// No refresh token (review D3, 2026-10-09): a refresh token is single-use and
// belongs to the origin that signed in; nothing that walks these lists may move it.
export const TOKEN_KEYS = [
  'symbols_access_token',
  'symbols_expires_at',
  'symbols_expires_in',
  'symbols_bridge_access_token',
  'symbols_bridge_expires_at'
]
// Frank-safe SELF-CONTAINED copy, not `_crossApp.collectSdkTokens`: a
// module-scope read off the stubbed `@symbo.ls/sdk` import serialized to ""
// in the published functions bag (the canvas/workspace `/_session-bridge`
// pages call `_wsShared.functions.collectSdkTokens()` and got an empty
// string live — WORKSPACE-SHELL-UNDECLARED-IMPORTS-1). Inline copy of the
// SDK's collectLocalSession over DEFAULT_TOKEN_KEYS
// (sdk/src/crossAppAuth.js) — the per-export self-contained-copy convention
// functions/sdk.js documents. Keep the key list in sync with the SDK.
export const collectSdkTokens = () => {
  if (typeof localStorage === 'undefined') return null
  const keys = [
    'symbols_access_token',
    'symbols_expires_at',
    'symbols_expires_in',
    'symbols_bridge_access_token',
    'symbols_bridge_expires_at'
  ]
  const out = {}
  let hasAny = false
  for (const k of keys) {
    const v = localStorage.getItem(k)
    if (v != null) {
      out[k] = v
      hasAny = true
    }
  }
  return hasAny ? out : null
}
